/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

// The bundled build is self-contained; the package main entry instead
// requires the optional `web-worker` dependency, which breaks the webpack
// build. Main-thread layout is fine at these sizes (tens of ingress nodes;
// low thousands of symbols for the knowledge-graph compound layout).
import type { ElkExtendedEdge, ElkNode } from 'elkjs/lib/elk.bundled.js';

export type ElkPoint = { x: number; y: number };

// ELK layered + orthogonal: ELK owns node placement AND edge routing,
// replacing dagre positions + the hand-rolled corridor router whose global
// lane ranking saturated on dense graphs (PLAT-662 ELK spike). Runs on the
// main thread (no worker): the ingress graph is tens of nodes, and layout
// completes in single-digit milliseconds at that size.
//
// Loaded lazily: the engine is ~1.4 MB of transpiled Java and only the
// network-policy and knowledge-graph pages need it, so it must not ride in
// the main bundle.
// runElkLayout is already async, so callers never notice the difference.
let elkInstance: Promise<InstanceType<typeof import('elkjs/lib/elk.bundled.js').default>> | null =
  null;
const getElk = () => {
  if (!elkInstance) {
    elkInstance = import('elkjs/lib/elk.bundled.js')
      .then(({ default: ElkCtor }) => new ElkCtor())
      .catch(err => {
        // A failed chunk load (redeploy invalidating asset hashes, flaky
        // network) must not poison the memo with a forever-rejected
        // promise; clearing it lets the next call retry the import.
        elkInstance = null;
        throw err;
      });
  }
  return elkInstance;
};

// Real Web Worker variant for the knowledge-graph compound layout: the same
// GWT engine bytes run inside elk-worker.min.js, so results are identical —
// only the thread differs. elkjs's PromisedWorker never attaches an onerror
// handler and terminateWorker() leaves in-flight resolvers dangling, so the
// memo carries an explicit `failed` promise (rejected by the worker error
// event) for callers to race against; without it a blocked worker chunk
// would hang layout forever behind the status line.
type ElkEngine = {
  layout(graph: ElkNode): Promise<ElkNode>;
  terminateWorker?: () => void;
};
type ElkWorkerMemo = { engine: Promise<ElkEngine>; failed: Promise<never> };
let elkWorkerMemo: ElkWorkerMemo | null = null;
const getElkWorker = (): ElkWorkerMemo => {
  if (!elkWorkerMemo) {
    let failReject: (e: Error) => void = () => {};
    const failed = new Promise<never>((_, rej) => {
      failReject = rej;
    });
    // Hygiene: nobody may be racing when the rejection lands.
    failed.catch(() => {});
    const engine = import('elkjs/lib/elk-api.js')
      .then(
        ({ default: ElkCtor }) =>
          new ElkCtor({
            workerFactory: () => {
              const w = new Worker(
                new URL('elkjs/lib/elk-worker.min.js', import.meta.url),
                { name: 'elk-layout' },
              );
              w.addEventListener('error', ev => {
                // Dispose so Retry spawns a fresh worker instead of reusing
                // a dead one; a runtime-errored worker is not auto-killed by
                // the browser, so terminate it explicitly.
                elkWorkerMemo = null;
                w.terminate();
                failReject(
                  new Error(
                    `elk worker failed: ${ev.message || 'script load/runtime error'}`,
                  ),
                );
              });
              return w;
            },
            // ELK.layout<T>'s self-referential generic return type cannot
            // be reconciled with the plain (ElkNode) => Promise<ElkNode>
            // shape; every field the code consumes exists on both types.
          }) as unknown as ElkEngine,
      )
      .catch(err => {
        // Same clear-memo-on-failure contract as getElk.
        elkWorkerMemo = null;
        throw err;
      });
    elkWorkerMemo = { engine, failed };
  }
  return elkWorkerMemo;
};

// Terminating is what actually stops a minutes-long in-flight layout; the
// abort race in runElkCompoundLayout is what lets the await settle after.
// Sole-consumer assumption: RepoGraph serializes layouts, so disposing the
// shared memo on abort is safe; a second concurrent consumer would need
// per-call workers or refcounting.
export const disposeElkWorker = () => {
  const memo = elkWorkerMemo;
  elkWorkerMemo = null;
  if (memo) {
    memo.engine.then(e => e.terminateWorker?.()).catch(() => {});
  }
};

const LAYOUT_OPTIONS: Record<string, string> = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.edgeRouting': 'ORTHOGONAL',
  // Between-layer gap hosts the vertical edge corridors; ELK widens it
  // per-layer as edge count grows, unlike the fixed dagre ranksep.
  'elk.layered.spacing.nodeNodeBetweenLayers': '140',
  'elk.spacing.nodeNode': '48',
  'elk.spacing.edgeNode': '24',
  'elk.spacing.edgeEdge': '14',
  'elk.layered.spacing.edgeNodeBetweenLayers': '24',
  'elk.layered.spacing.edgeEdgeBetweenLayers': '14',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  // Disconnected components (services with no edges) are packed side by
  // side; give them explicit breathing room.
  'elk.spacing.componentComponent': '48',
};

export type ElkLayoutResult = {
  positions: Map<string, ElkPoint>;
  // Edge id -> orthogonal polyline (start, bends..., end), in the same
  // absolute coordinate space as the node positions.
  routes: Map<string, ElkPoint[]>;
};

export const runElkLayout = async (args: {
  nodes: { id: string; width: number; height: number }[];
  edges: { id: string; source: string; target: string }[];
}): Promise<ElkLayoutResult> => {
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: LAYOUT_OPTIONS,
    children: args.nodes.map(n => ({
      id: n.id,
      width: n.width,
      height: n.height,
    })),
    edges: args.edges.map(
      (e): ElkExtendedEdge => ({
        id: e.id,
        sources: [e.source],
        targets: [e.target],
      }),
    ),
  };
  const out = await (await getElk()).layout(graph);
  const positions = new Map<string, ElkPoint>();
  for (const child of out.children ?? []) {
    positions.set(child.id, { x: child.x ?? 0, y: child.y ?? 0 });
  }
  const routes = new Map<string, ElkPoint[]>();
  for (const edge of out.edges ?? []) {
    // Flat graph -> a single section per edge.
    const sec = edge.sections?.[0];
    if (!sec) continue;
    routes.set(edge.id, [
      sec.startPoint,
      ...(sec.bendPoints ?? []),
      sec.endPoint,
    ]);
  }
  return { positions, routes };
};

export type ElkRect = { x: number; y: number; width: number; height: number };

export type ElkCompoundLayoutResult = {
  // Absolute top-left per leaf node (group members and loose nodes alike).
  positions: Map<string, ElkPoint>;
  groupBounds: Map<string, ElkRect>;
  // Edge id -> absolute polyline. Edges may target a group id directly.
  routes: Map<string, ElkPoint[]>;
  width: number;
  height: number;
};

type CompoundLayoutArgs = {
  groups: { id: string; children: { id: string; width: number; height: number }[] }[];
  nodes: { id: string; width: number; height: number }[];
  edges: { id: string; source: string; target: string }[];
  // Group-box padding; top hosts the group's header label.
  padding: { top: number; side: number; bottom: number };
};

const COMPONENT_GAP = 48;
// Roughly landscape-viewport shaped; the shelf packer below aims total
// bounds at this ratio instead of ELK's single-graph result, which stacks
// disconnected components into one tall ribbon.
const COMPONENT_ASPECT = 1.8;

// Weakly-connected components over top-level units (a group and its members
// are one unit). Cross-component edges cannot exist by construction, so each
// component lays out and translates independently.
export const splitComponents = (args: CompoundLayoutArgs): CompoundLayoutArgs[] => {
  const unitOf = new Map<string, string>();
  for (const g of args.groups) {
    unitOf.set(g.id, g.id);
    for (const c of g.children) unitOf.set(c.id, g.id);
  }
  for (const n of args.nodes) unitOf.set(n.id, n.id);

  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r) !== undefined && parent.get(r) !== r) r = parent.get(r)!;
    parent.set(x, r);
    return r;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  unitOf.forEach(u => parent.set(u, u));
  for (const e of args.edges) {
    const s = unitOf.get(e.source);
    const t = unitOf.get(e.target);
    if (s && t) union(s, t);
  }

  const buckets = new Map<string, CompoundLayoutArgs>();
  const bucketFor = (unit: string) => {
    const root = find(unit);
    let b = buckets.get(root);
    if (!b) {
      b = { groups: [], nodes: [], edges: [], padding: args.padding };
      buckets.set(root, b);
    }
    return b;
  };
  for (const g of args.groups) bucketFor(g.id).groups.push(g);
  for (const n of args.nodes) bucketFor(n.id).nodes.push(n);
  for (const e of args.edges) {
    const u = unitOf.get(e.source);
    if (u) bucketFor(u).edges.push(e);
  }
  return [...buckets.values()];
};

// Shelf-pack component bounding boxes into rows aiming at COMPONENT_ASPECT.
// Returns one {x, y} offset per component, in input order.
export const packComponents = (
  sizes: { width: number; height: number }[],
  gap = COMPONENT_GAP,
  aspect = COMPONENT_ASPECT,
): { x: number; y: number }[] => {
  const order = sizes
    .map((s, i) => ({ ...s, i }))
    .sort((a, b) => b.height - a.height);
  const totalArea = order.reduce((a, s) => a + (s.width + gap) * (s.height + gap), 0);
  const targetWidth = Math.max(
    Math.sqrt(totalArea * aspect),
    ...order.map(s => s.width),
  );
  const offsets = new Array<{ x: number; y: number }>(sizes.length);
  let x = 0;
  let y = 0;
  let rowH = 0;
  for (const s of order) {
    if (x > 0 && x + s.width > targetWidth) {
      x = 0;
      y += rowH + gap;
      rowH = 0;
    }
    offsets[s.i] = { x, y };
    x += s.width + gap;
    rowH = Math.max(rowH, s.height);
  }
  return offsets;
};

const layoutOneComponent = async (
  args: CompoundLayoutArgs,
  engine: ElkEngine,
): Promise<ElkCompoundLayoutResult> => {
  const { top, side, bottom } = args.padding;
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: {
      ...LAYOUT_OPTIONS,
      'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    },
    children: [
      ...args.groups.map(g => ({
        id: g.id,
        layoutOptions: {
          'elk.padding': `[top=${top},left=${side},bottom=${bottom},right=${side}]`,
        },
        children: g.children.map(c => ({ ...c })),
      })),
      ...args.nodes.map(n => ({ ...n })),
    ],
    edges: args.edges.map(
      (e): ElkExtendedEdge => ({
        id: e.id,
        sources: [e.source],
        targets: [e.target],
      }),
    ),
  };
  const out = await engine.layout(graph);

  const positions = new Map<string, ElkPoint>();
  const groupBounds = new Map<string, ElkRect>();
  // Absolute origin per container id, for converting edge routes.
  const origins = new Map<string, ElkPoint>([['root', { x: 0, y: 0 }]]);
  let width = 0;
  let height = 0;
  for (const child of out.children ?? []) {
    const cx = child.x ?? 0;
    const cy = child.y ?? 0;
    width = Math.max(width, cx + (child.width ?? 0));
    height = Math.max(height, cy + (child.height ?? 0));
    if (child.children?.length) {
      groupBounds.set(child.id, {
        x: cx,
        y: cy,
        width: child.width ?? 0,
        height: child.height ?? 0,
      });
      origins.set(child.id, { x: cx, y: cy });
      for (const leaf of child.children) {
        positions.set(leaf.id, { x: cx + (leaf.x ?? 0), y: cy + (leaf.y ?? 0) });
      }
    } else {
      positions.set(child.id, { x: cx, y: cy });
    }
  }

  const routes = new Map<string, ElkPoint[]>();
  for (const edge of out.edges ?? []) {
    const origin =
      origins.get((edge as { container?: string }).container ?? 'root') ??
      { x: 0, y: 0 };
    const pts: ElkPoint[] = [];
    for (const sec of edge.sections ?? []) {
      for (const p of [sec.startPoint, ...(sec.bendPoints ?? []), sec.endPoint]) {
        pts.push({ x: p.x + origin.x, y: p.y + origin.y });
      }
    }
    if (pts.length) routes.set(edge.id, pts);
  }

  return { positions, groupBounds, routes, width, height };
};

// One-level compound layout for the knowledge-graph symbol altitude: groups
// are file/module containers, children are the symbols inside them. Same
// engine and options as the flat layout; INCLUDE_CHILDREN makes ELK place
// and route across the hierarchy in one pass. Coordinate semantics (verified
// against elkjs 0.11 output): child x/y are relative to their parent, and
// each edge's section points are relative to the node named by its
// `container` property (root or a group — one nesting level, so never a leaf).
//
// Disconnected components are laid out separately and shelf-packed toward a
// landscape aspect ratio — ELK's own packing is unavailable under
// INCLUDE_CHILDREN and left the graph a single tall ribbon.
// ELK's GWT internals recurse with component size; the worker thread's
// stack is smaller than the main thread's, so a graph class that laid out
// fine on the main thread (proven in prod up to ~2.4k nodes) can overflow
// in the worker. Chrome/Safari and Firefox wordings.
export const isStackOverflowError = (e: unknown): boolean =>
  /maximum call stack|stack size exceeded|too much recursion/i.test(
    String((e as Error | undefined)?.message ?? e),
  );

export const countLeafUnits = (args: {
  groups: { children: unknown[] }[];
  nodes: unknown[];
}): number =>
  args.groups.reduce((a, g) => a + g.children.length, 0) + args.nodes.length;

// Fallback ceiling: covers every graph size that worked on the main thread
// before the worker change (largest proven ~2.4k nodes), while giants that
// never completed anywhere keep the fast banner + Retry instead of a
// minutes-long frozen tab.
const FALLBACK_MAX_UNITS = 3000;

export type CompoundLayoutOpts = {
  // Aborting disposes the worker (the only way to stop an in-flight ELK
  // run) and rejects with an AbortError the caller's cleanup already hides.
  signal?: AbortSignal;
  onProgress?: (done: number, total: number) => void;
  // Fired before the bounded main-thread rerun: lets the caller announce
  // the coming freeze before the thread stops painting.
  onFallback?: () => void;
};

export const runElkCompoundLayout = async (
  args: CompoundLayoutArgs,
  opts: CompoundLayoutOpts = {},
): Promise<ElkCompoundLayoutResult> => {
  // jsdom/jest have no Worker: tests and any workerless context keep the
  // exact main-thread bundled path.
  if (typeof Worker === 'undefined') return runCompound(args, opts, false);
  try {
    return await runCompound(args, opts, true);
  } catch (e) {
    if (
      isStackOverflowError(e) &&
      countLeafUnits(args) <= FALLBACK_MAX_UNITS &&
      !opts.signal?.aborted
    ) {
      // The overflowed worker is useless — spawn fresh next time. Rerun on
      // the main thread (pre-change behavior for exactly the graph class
      // that used to work there; blocks the tab briefly, bounded by the
      // unit ceiling).
      disposeElkWorker();
      opts.onFallback?.();
      // One frame so the announcement actually paints before the freeze.
      await new Promise(r => setTimeout(r, 50));
      return runCompound(args, opts, false);
    }
    throw e;
  }
};

const runCompound = async (
  args: CompoundLayoutArgs,
  opts: CompoundLayoutOpts,
  useWorker: boolean,
): Promise<ElkCompoundLayoutResult> => {
  const { signal, onProgress } = opts;
  const throwIfAborted = () => {
    if (signal?.aborted) throw new DOMException('layout aborted', 'AbortError');
  };
  throwIfAborted();
  const memo = useWorker ? getElkWorker() : null;
  let abortReject: ((e: Error) => void) | null = null;
  const abortPromise = signal
    ? new Promise<never>((_, rej) => {
        abortReject = rej;
      })
    : null;
  abortPromise?.catch(() => {});
  const onAbort = () => {
    disposeElkWorker();
    abortReject?.(new DOMException('layout aborted', 'AbortError'));
  };
  signal?.addEventListener('abort', onAbort);
  try {
    const races: Promise<never>[] = [];
    if (memo) races.push(memo.failed);
    if (abortPromise) races.push(abortPromise);
    const engine: ElkEngine = memo
      ? await Promise.race([memo.engine, ...races])
      : await getElk();
    const one = (c: CompoundLayoutArgs) =>
      races.length
        ? Promise.race([layoutOneComponent(c, engine), ...races])
        : layoutOneComponent(c, engine);
    const components = splitComponents(args);
    if (components.length > 1) {
      const results: ElkCompoundLayoutResult[] = [];
      for (const [i, c] of components.entries()) {
        throwIfAborted();
        results.push(await one(c));
        onProgress?.(i + 1, components.length);
      }
      const offsets = packComponents(results);
      const merged: ElkCompoundLayoutResult = {
        positions: new Map(),
        groupBounds: new Map(),
        routes: new Map(),
        width: 0,
        height: 0,
      };
      results.forEach((r, i) => {
        const { x: dx, y: dy } = offsets[i];
        r.positions.forEach((p, id) =>
          merged.positions.set(id, { x: p.x + dx, y: p.y + dy }),
        );
        r.groupBounds.forEach((b, id) =>
          merged.groupBounds.set(id, { ...b, x: b.x + dx, y: b.y + dy }),
        );
        r.routes.forEach((pts, id) =>
          merged.routes.set(id, pts.map(p => ({ x: p.x + dx, y: p.y + dy }))),
        );
        merged.width = Math.max(merged.width, dx + r.width);
        merged.height = Math.max(merged.height, dy + r.height);
      });
      return merged;
    }
    const res = await one(args);
    onProgress?.(1, 1);
    return res;
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
};
